import React, { useEffect, useRef, useState } from 'react';

// Drop-in Element da Airwallex. Faz o papel do <Elements> do Stripe: o SDK
// mostra os métodos que estiverem ATIVOS NA CONTA daquele corp (cartão,
// Multibanco, ...), trata da confirmação e, quando o método precisa de sair da
// página — como o Multibanco, que manda o cliente para a página de instruções
// deles —, é o próprio SDK que redireciona.
//
// env: 'demo' na sandbox, 'prod' em produção. Vem do gateway no lugar que o
// Stripe usa para a publishable key.
export default function AirwallexForm({ env, intentId, clientSecret, currency, returnUrl }) {
  const containerRef = useRef(null);
  const montado = useRef(false);
  const jaConcluido = useRef(false);

  const [erro, setErro] = useState('');
  const [pronto, setPronto] = useState(false);

  useEffect(() => {
    // O React 18+ em modo estrito monta duas vezes: sem esta guarda apareciam
    // dois formulários.
    if (montado.current) return;
    montado.current = true;

    let elemento = null;
    let cancelado = false;
    let limparWindow = null;

    const montar = async () => {
      try {
        const { init, createElement } = await import('@airwallex/components-sdk');

        await init({ env, enabledElements: ['payments'] });

        if (cancelado) return;

        elemento = await createElement('dropIn', {
          intent_id: intentId,
          client_secret: clientSecret,
          currency: (currency || 'EUR').toUpperCase(),
          mode: 'payment',
          appearance: { variables: { colorBrand: '#171e2e' } }
        });

        if (cancelado) return;

        // Pagamento concluído sem sair da página (cartão). Nos métodos que
        // levam o cliente para fora, como o Multibanco, é o SDK que
        // redireciona e isto não chega a correr.
        const concluir = () => {
          if (jaConcluido.current) return;
          jaConcluido.current = true;

          console.info('[airwallex] sucesso -> a ir para', returnUrl);
          window.location.replace(returnUrl);
        };

        const falhar = (erro) => {
          console.error('[airwallex] erro:', erro);
          setErro('Não foi possível concluir o pagamento, tente mais tarde.');
        };

        // O SDK entrega os eventos no WINDOW, não no elemento.
        //
        // Confirmado numa passagem real em 2026-09-24, com o Airwallex Checkout
        // UI v1.1272.0: o elemento.on() existe mas nunca dispara; onReady e
        // onSuccess chegam como eventos do window. Se um dia isso mudar, o
        // sintoma é o pagamento concluir e a página não avançar — e os logs
        // abaixo dizem-no logo.
        //
        // O window é global: os eventos disparam repetidos (vimos onReady três
        // vezes e onSuccess duas), daí a guarda no concluir().
        const aoWindow = (evento) => {
          console.info(`[airwallex] ${evento.type}`, evento?.detail ?? '');

          if (evento.type === 'onReady') setPronto(true);
          if (evento.type === 'onSuccess') concluir();
          if (evento.type === 'onError') falhar(evento?.detail?.error);
        };

        const NOMES = ['onReady', 'onSuccess', 'onError', 'onCancel'];
        NOMES.forEach((nome) => window.addEventListener(nome, aoWindow));
        limparWindow = () => NOMES.forEach((nome) => window.removeEventListener(nome, aoWindow));

        elemento.mount(containerRef.current);
      } catch (error) {
        console.error('Falha ao montar o checkout Airwallex:', error);
        if (!cancelado) setErro('Não foi possível carregar o pagamento, tente mais tarde.');
      }
    };

    montar();

    return () => {
      cancelado = true;
      limparWindow?.();
      try { elemento?.unmount?.(); } catch (error) { /* o elemento já não existe */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="airwallexForm">
      {!pronto && !erro && <p className="checkoutSubtitle">A carregar os métodos de pagamento...</p>}
      <div ref={containerRef} />
      {erro && <div className="error">{erro}</div>}
    </div>
  );
}
